import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Filter, RefreshCw } from 'lucide-react';
import { api, PartnerPerformanceRow, reportsApi } from '../api';

interface PartnerOption {
  mp_id: number;
  name: string;
}

interface CenterOption {
  center_id: number;
  name: string;
}

interface AppliedFilters {
  partnerId?: number;
  centerId?: number;
  from?: string;
  to?: string;
  threshold: number;
}

const DEFAULT_THRESHOLD = 70;

const ReportsPartnerPerformance: React.FC = () => {
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [centers, setCenters] = useState<CenterOption[]>([]);

  const [rows, setRows] = useState<PartnerPerformanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thresholdMeta, setThresholdMeta] = useState(DEFAULT_THRESHOLD);

  const [inputFilters, setInputFilters] = useState({
    partnerId: '',
    centerId: '',
    from: '',
    to: '',
    threshold: DEFAULT_THRESHOLD.toString(),
  });

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({
    threshold: DEFAULT_THRESHOLD,
  });

  useEffect(() => {
    fetchPartners();
    fetchCenters();
  }, []);

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const fetchPartners = async () => {
    try {
      const response = await api.get<{ success?: boolean; data?: PartnerOption[] }>('/partners');
      if (Array.isArray(response.data?.data)) {
        const data = response.data.data.slice().sort((a, b) => a.name.localeCompare(b.name));
        setPartners(data);
      }
    } catch (err) {
      console.error('Failed to load partners for report filters.', err);
    }
  };

  const fetchCenters = async () => {
    try {
      const response = await api.get<{ success?: boolean; data?: CenterOption[] }>('/centers');
      if (Array.isArray(response.data?.data)) {
        const data = response.data.data.slice().sort((a, b) => a.name.localeCompare(b.name));
        setCenters(data);
      }
    } catch (err) {
      console.error('Failed to load centers for report filters.', err);
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: AppliedFilters = {
        threshold: appliedFilters.threshold,
      };

      if (appliedFilters.partnerId != null) params.partnerId = appliedFilters.partnerId;
      if (appliedFilters.centerId != null) params.centerId = appliedFilters.centerId;
      if (appliedFilters.from) params.from = appliedFilters.from;
      if (appliedFilters.to) params.to = appliedFilters.to;

      const response = await reportsApi.getPartnerPerformance(params);
      setRows(response.data);
      setThresholdMeta(response.meta.threshold);
    } catch (err: any) {
      console.error('Failed to fetch partner performance report.', err);
      const message = err?.response?.data?.message ?? 'Failed to fetch report data.';
      setError(message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const partnerId = inputFilters.partnerId ? Number(inputFilters.partnerId) : undefined;
    const centerId = inputFilters.centerId ? Number(inputFilters.centerId) : undefined;
    const thresholdRaw = Number(inputFilters.threshold);
    const threshold = Number.isFinite(thresholdRaw) ? Math.max(0, Math.min(100, thresholdRaw)) : DEFAULT_THRESHOLD;

    setAppliedFilters({
      partnerId,
      centerId,
      from: inputFilters.from || undefined,
      to: inputFilters.to || undefined,
      threshold,
    });
  };

  const handleReset = () => {
    setInputFilters({
      partnerId: '',
      centerId: '',
      from: '',
      to: '',
      threshold: DEFAULT_THRESHOLD.toString(),
    });
    setAppliedFilters({
      threshold: DEFAULT_THRESHOLD,
    });
  };

  const activeThreshold = useMemo(() => appliedFilters.threshold ?? thresholdMeta, [appliedFilters.threshold, thresholdMeta]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link
            to="/dashboard/reports"
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Reports
          </Link>
          <h1 className="mt-3 text-2xl font-bold text-gray-900">Partner Performance Report</h1>
          <p className="mt-1 text-sm text-gray-700">
            Review mobilisation partner outcomes, completion rates, and quickly spot underperformers.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={fetchReport}
          disabled={loading}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <form className="grid grid-cols-1 gap-4 sm:grid-cols-5" onSubmit={handleFilterSubmit}>
          <div className="sm:col-span-1">
            <label htmlFor="partner-filter" className="form-label">
              Partner
            </label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                id="partner-filter"
                className="form-input pl-10"
                value={inputFilters.partnerId}
                onChange={(event) => setInputFilters((prev) => ({ ...prev, partnerId: event.target.value }))}
              >
                <option value="">All partners</option>
                {partners.map((partner) => (
                  <option key={partner.mp_id} value={partner.mp_id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="sm:col-span-1">
            <label htmlFor="center-filter" className="form-label">
              Center
            </label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                id="center-filter"
                className="form-input pl-10"
                value={inputFilters.centerId}
                onChange={(event) => setInputFilters((prev) => ({ ...prev, centerId: event.target.value }))}
              >
                <option value="">All centers</option>
                {centers.map((center) => (
                  <option key={center.center_id} value={center.center_id}>
                    {center.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="sm:col-span-1">
            <label htmlFor="from-date" className="form-label">
              From date
            </label>
            <input
              id="from-date"
              type="date"
              className="form-input"
              value={inputFilters.from}
              onChange={(event) => setInputFilters((prev) => ({ ...prev, from: event.target.value }))}
            />
          </div>

          <div className="sm:col-span-1">
            <label htmlFor="to-date" className="form-label">
              To date
            </label>
            <input
              id="to-date"
              type="date"
              className="form-input"
              value={inputFilters.to}
              onChange={(event) => setInputFilters((prev) => ({ ...prev, to: event.target.value }))}
            />
          </div>

          <div className="sm:col-span-1">
            <label htmlFor="threshold" className="form-label">
              Threshold (%)
            </label>
            <input
              id="threshold"
              type="number"
              min={0}
              max={100}
              className="form-input"
              value={inputFilters.threshold}
              onChange={(event) => setInputFilters((prev) => ({ ...prev, threshold: event.target.value }))}
            />
          </div>

          <div className="sm:col-span-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-end">
            <button type="submit" className="btn-secondary sm:w-auto">
              Apply filters
            </button>
            <button
              type="button"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition sm:w-auto"
              onClick={handleReset}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </form>

        {error && (
          <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 mt-4">
            {error}
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-medium text-gray-900">
            Partner summary ({rows.length})
          </h2>
          <div className="text-sm text-gray-600">
            Threshold: <span className="font-medium">{activeThreshold.toFixed(2)}%</span>
          </div>
        </div>

        {loading ? (
          <div className="px-6 py-12 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : rows.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Partner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batches allocated</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batches completed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completion rate</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-sm text-gray-700">
                {rows.map((row) => (
                  <tr key={row.mpId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">{row.partnerName}</div>
                    </td>
                    <td className="px-6 py-4">{row.allocated}</td>
                    <td className="px-6 py-4">{row.completed}</td>
                    <td className={`px-6 py-4 font-medium ${row.belowThreshold ? 'text-red-600' : 'text-gray-900'}`}>
                      {row.completionRate.toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-12 text-center text-sm text-gray-500">
            No partners match the current filters.
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPartnerPerformance;
